import React, { useState, useEffect, useCallback } from 'react';
import { sessionsAPI } from '../../../../services/api';
//import './Sessions.css';

function Sessions({ campaign }) {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('list'); // list, create, edit
  const [selectedSession, setSelectedSession] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state for new/editing session
  const [formData, setFormData] = useState({
    sessionNumber: '',
    sessionDate: '',
    title: '',
    summary: '',
    notes: '',
    plannedEncounters: [],
    actualEvents: [],
    npcsPresent: [],
    rewardsGiven: { xp: 0, items: [] },
    status: 'Planned'
  });

  const loadSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await sessionsAPI.getCampaignSessions(campaign.id);
      setSessions(res.data?.sessions || []);
    } catch (e) {
      console.error('Failed to load sessions:', e);
      setError(e?.response?.data?.error || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [campaign.id]);


  useEffect(() => {
    loadSessions();
  }, [campaign.id, loadSessions]);

  const resetForm = () => {
    setFormData({
      sessionNumber: sessions.length + 1,
      sessionDate: '',
      title: '',
      summary: '',
      notes: '',
      plannedEncounters: [],
      actualEvents: [],
      npcsPresent: [],
      rewardsGiven: { xp: 0, items: [] },
      status: 'Planned'
    });
  };

  const handleCreateNew = () => {
    resetForm();
    setActiveView('create');
    setSelectedSession(null);
  };

  const handleEditSession = (session) => {
    setSelectedSession(session);
    setFormData({
      sessionNumber: session.sessionNumber,
      sessionDate: session.sessionDate ? new Date(session.sessionDate).toISOString().split('T')[0] : '',
      title: session.title,
      summary: session.summary || '',
      notes: session.notes || '',
      plannedEncounters: session.plannedEncounters ? JSON.parse(session.plannedEncounters) : [],
      actualEvents: session.actualEvents ? JSON.parse(session.actualEvents) : [],
      npcsPresent: session.npcsPresent ? JSON.parse(session.npcsPresent) : [],
      rewardsGiven: session.rewardsGiven ? JSON.parse(session.rewardsGiven) : { xp: 0, items: [] },
      status: session.status
    });
    setActiveView('edit');
  };

  const handleSaveSession = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        ...formData,
        sessionDate: formData.sessionDate || null,
        plannedEncounters: JSON.stringify(formData.plannedEncounters),
        actualEvents: JSON.stringify(formData.actualEvents),
        npcsPresent: JSON.stringify(formData.npcsPresent),
        rewardsGiven: JSON.stringify(formData.rewardsGiven)
      };

      if (activeView === 'create') {
        await sessionsAPI.createSession(campaign.id, payload);
      } else {
        await sessionsAPI.updateSession(selectedSession.id, payload);
      }

      await loadSessions();
      setActiveView('list');
      resetForm();
    } catch (e) {
      console.error('Failed to save session:', e);
      setError(e?.response?.data?.error || 'Failed to save session');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session? This cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      await sessionsAPI.deleteSession(sessionId);
      await loadSessions();
    } catch (e) {
      console.error('Failed to delete session:', e);
      setError(e?.response?.data?.error || 'Failed to delete session');
    }
  };

  const addEncounter = () => {
    setFormData({
      ...formData,
      plannedEncounters: [...formData.plannedEncounters, { name: '', difficulty: 'Easy', notes: '' }]
    });
  };

  const updateEncounter = (index, field, value) => {
    const updated = [...formData.plannedEncounters];
    updated[index][field] = value;
    setFormData({ ...formData, plannedEncounters: updated });
  };

  const removeEncounter = (index) => {
    setFormData({
      ...formData,
      plannedEncounters: formData.plannedEncounters.filter((_, i) => i !== index)
    });
  };

  const addEvent = () => {
    setFormData({
      ...formData,
      actualEvents: [...formData.actualEvents, { description: '', important: false }]
    });
  };

  const updateEvent = (index, field, value) => {
    const updated = [...formData.actualEvents];
    updated[index][field] = value;
    setFormData({ ...formData, actualEvents: updated });
  };

  const removeEvent = (index) => {
    setFormData({
      ...formData,
      actualEvents: formData.actualEvents.filter((_, i) => i !== index)
    });
  };

  const addRewardItem = () => {
    setFormData({
      ...formData,
      rewardsGiven: {
        ...formData.rewardsGiven,
        items: [...formData.rewardsGiven.items, '']
      }
    });
  };

  const updateRewardItem = (index, value) => {
    const updated = [...formData.rewardsGiven.items];
    updated[index] = value;
    setFormData({
      ...formData,
      rewardsGiven: { ...formData.rewardsGiven, items: updated }
    });
  };

  const removeRewardItem = (index) => {
    setFormData({
      ...formData,
      rewardsGiven: {
        ...formData.rewardsGiven,
        items: formData.rewardsGiven.items.filter((_, i) => i !== index)
      }
    });
  };

  const renderSessionList = () => (
    <div className="sessions-list-view">
      <div className="sessions-header">
        <h3>Game Sessions</h3>
        <button className="btn-primary" onClick={handleCreateNew}>
          + New Session
        </button>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state">
          <p>No sessions planned yet</p>
          <button className="btn-secondary" onClick={handleCreateNew}>
            Create Your First Session
          </button>
        </div>
      ) : (
        <div className="sessions-grid">
          {sessions.map(session => (
            <div key={session.id} className={`session-card status-${session.status.toLowerCase().replace(' ', '-')}`}>
              <div className="session-card-header">
                <div className="session-title-row">
                  <span className="session-number">Session #{session.sessionNumber}</span>
                  <span className={`session-status ${session.status.toLowerCase().replace(' ', '-')}`}>
                    {session.status}
                  </span>
                </div>
                <h4>{session.title}</h4>
                {session.sessionDate && (
                  <div className="session-date">
                    📅 {new Date(session.sessionDate).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div className="session-card-body">
                {session.summary && (
                  <p className="session-summary">{session.summary}</p>
                )}

                <div className="session-stats">
                  {session.plannedEncounters && JSON.parse(session.plannedEncounters).length > 0 && (
                    <div className="session-stat">
                      ⚔️ {JSON.parse(session.plannedEncounters).length} Encounters
                    </div>
                  )}
                  {session.npcsPresent && JSON.parse(session.npcsPresent).length > 0 && (
                    <div className="session-stat">
                      🎭 {JSON.parse(session.npcsPresent).length} NPCs
                    </div>
                  )}
                  {session.rewardsGiven && JSON.parse(session.rewardsGiven).xp > 0 && (
                    <div className="session-stat">
                      ⭐ {JSON.parse(session.rewardsGiven).xp} XP
                    </div>
                  )}
                </div>
              </div>

              <div className="session-card-actions">
                <button className="btn-secondary" onClick={() => handleEditSession(session)}>
                  Edit
                </button>
                <button className="btn-delete" onClick={() => handleDeleteSession(session.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSessionForm = () => (
    <div className="session-form-view">
      <div className="form-header">
        <h3>{activeView === 'create' ? 'Create New Session' : 'Edit Session'}</h3>
        <div className="form-actions">
          <button className="btn-secondary" onClick={() => setActiveView('list')} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSaveSession} disabled={isSaving || !formData.title}>
            {isSaving ? 'Saving...' : 'Save Session'}
          </button>
        </div>
      </div>

      <div className="session-form">
        <div className="form-section">
          <h4>Basic Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Session Number</label>
              <input
                type="number"
                value={formData.sessionNumber}
                onChange={(e) => setFormData({ ...formData, sessionNumber: parseInt(e.target.value) })}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>Session Date</label>
              <input
                type="date"
                value={formData.sessionDate}
                onChange={(e) => setFormData({ ...formData, sessionDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="Planned">Planned</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Session Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., The Mystery of the Ruined Temple"
              required
            />
          </div>

          <div className="form-group">
            <label>Summary</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              placeholder="Brief overview of what happens in this session..."
              rows={3}
            />
          </div>
        </div>

        <div className="form-section">
          <h4>Planned Encounters</h4>
          {formData.plannedEncounters.map((encounter, index) => (
            <div key={index} className="encounter-item">
              <div className="encounter-header">
                <input
                  type="text"
                  placeholder="Encounter name"
                  value={encounter.name}
                  onChange={(e) => updateEncounter(index, 'name', e.target.value)}
                />
                <select
                  value={encounter.difficulty}
                  onChange={(e) => updateEncounter(index, 'difficulty', e.target.value)}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                  <option value="Deadly">Deadly</option>
                </select>
                <button className="btn-delete" onClick={() => removeEncounter(index)}>×</button>
              </div>
              <textarea
                placeholder="Encounter notes..."
                value={encounter.notes}
                onChange={(e) => updateEncounter(index, 'notes', e.target.value)}
                rows={2}
              />
            </div>
          ))}
          <button className="btn-secondary" onClick={addEncounter}>
            + Add Encounter
          </button>
        </div>

        <div className="form-section">
          <h4>Actual Events (Post-Session)</h4>
          {formData.actualEvents.map((event, index) => (
            <div key={index} className="event-item">
              <div className="event-header">
                <input
                  type="text"
                  placeholder="Event description"
                  value={event.description}
                  onChange={(e) => updateEvent(index, 'description', e.target.value)}
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={event.important}
                    onChange={(e) => updateEvent(index, 'important', e.target.checked)}
                  />
                  Important
                </label>
                <button className="btn-delete" onClick={() => removeEvent(index)}>×</button>
              </div>
            </div>
          ))}
          <button className="btn-secondary" onClick={addEvent}>
            + Add Event
          </button>
        </div>

        <div className="form-section">
          <h4>Rewards Given</h4>
          <div className="form-group">
            <label>XP Awarded</label>
            <input
              type="number"
              value={formData.rewardsGiven.xp}
              onChange={(e) => setFormData({
                ...formData,
                rewardsGiven: { ...formData.rewardsGiven, xp: parseInt(e.target.value) || 0 }
              })}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Items</label>
            {formData.rewardsGiven.items.map((item, index) => (
              <div key={index} className="reward-item">
                <input
                  type="text"
                  placeholder="Item name/description"
                  value={item}
                  onChange={(e) => updateRewardItem(index, e.target.value)}
                />
                <button className="btn-delete" onClick={() => removeRewardItem(index)}>×</button>
              </div>
            ))}
            <button className="btn-secondary" onClick={addRewardItem}>
              + Add Item
            </button>
          </div>
        </div>

        <div className="form-section">
          <h4>GM Notes</h4>
          <div className="form-group">
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Private notes for the GM - what worked well, what to remember for next time, player reactions, etc."
              rows={5}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="sessions-section loading">
        <div className="spinner"></div>
        <p>Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className="sessions-section">
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {activeView === 'list' ? renderSessionList() : renderSessionForm()}
    </div>
  );
}

export default Sessions;
